import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import clsx from 'clsx';
import { getSettingsTooltipCopy } from './settingsTooltipCopy';

interface TooltipPosition {
  /** Set when placed BELOW the trigger. Mutually exclusive with `bottom`. */
  top?: number;
  /**
   * Set when placed ABOVE the trigger. Anchoring the bottom edge keeps the gap
   * exact without knowing the content height — see `recomputePosition`.
   */
  bottom?: number;
  left: number;
  placement: 'top' | 'bottom';
}

export interface HelpTooltipProps {
  label: string;
  content?: ReactNode;
  className?: string;
  id?: string;
  testId?: string;
  /**
   * When true the help icon is ALWAYS visible (the tooltip text still only
   * appears on hover/focus). Default false keeps the app-wide behaviour where
   * the icon is revealed only on field hover/focus. */
  alwaysVisible?: boolean;
}

const GAP = 8;
const MAX_WIDTH = 320;
const TOUCH_LONG_PRESS_MS = 450;

export function HelpTooltip({ label, content, className, id, testId, alwaysVisible = false }: HelpTooltipProps) {
  const generatedTooltipId = useId();
  const tooltipId = id ?? generatedTooltipId;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const longPressRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  const close = useCallback(() => setOpen(false), []);

  const recomputePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const tooltipWidth = Math.min(MAX_WIDTH, window.innerWidth - 24);
    const measuredHeight = tooltipRef.current?.offsetHeight ?? 72;
    const hasRoomAbove = rect.top >= measuredHeight + GAP + 8;
    const placement: TooltipPosition['placement'] = hasRoomAbove ? 'top' : 'bottom';
    const centeredLeft = rect.left + rect.width / 2 - tooltipWidth / 2;
    const left = Math.max(12, Math.min(centeredLeft, window.innerWidth - tooltipWidth - 12));

    // ATL-000729 — anchor the BOTTOM edge when placing above, never a `top`
    // derived from the height.
    //
    // On first paint the tooltip is not mounted, so `measuredHeight` is the 72px
    // fallback. Anchoring `top = rect.top - 72 - GAP` and then rendering taller
    // content (a multi-line tooltip is ~130px) pushed the tooltip's bottom edge
    // DOWN over the trigger. The tooltip is portaled to `document.body`, so it is
    // not a DOM descendant of the trigger: covering the cursor fired the
    // trigger's `mouseleave`, which closed the tooltip, which uncovered the
    // cursor, which fired `mouseenter` again — a visible flicker loop for any
    // content taller than the fallback.
    //
    // `bottom` is height-independent: the gap above the trigger is exact
    // whatever the content measures, so the tooltip can never overlap it and
    // there is nothing to oscillate. The downward placement was already
    // height-independent (`rect.bottom + GAP`) and is unchanged.
    if (placement === 'top') {
      setPosition({
        bottom: Math.max(8, window.innerHeight - rect.top + GAP),
        left,
        placement,
      });
      return;
    }
    setPosition({ top: Math.max(8, rect.bottom + GAP), left, placement });
  }, []);

  useEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }

    recomputePosition();
    const raf = requestAnimationFrame(recomputePosition);
    const onReposition = () => recomputePosition();
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || tooltipRef.current?.contains(target)) return;
      close();
    };

    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [close, open, recomputePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close();
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [close, open]);

  useEffect(() => () => {
    if (longPressRef.current) clearTimeout(longPressRef.current);
  }, []);

  const resolvedContent = content === undefined ? defaultSettingsHelp(label) : content;

  if (!resolvedContent) return null;

  const clearLongPress = () => {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
  };

  return (
    <span
      className={clsx(
        'inline-flex shrink-0 items-center transition-opacity duration-150 ease-out',
        alwaysVisible
          ? 'opacity-100 pointer-events-auto'
          : 'opacity-0 pointer-events-none group-hover/help-field:opacity-100 group-hover/help-field:pointer-events-auto group-focus-within/help-field:opacity-100 group-focus-within/help-field:pointer-events-auto',
        className,
      )}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-label={`Help: ${label}`}
        aria-expanded={open}
        aria-describedby={open ? tooltipId : undefined}
        data-testid={testId}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen(true);
        }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onTouchStart={(event) => {
          event.preventDefault();
          clearLongPress();
          longPressRef.current = setTimeout(() => setOpen(true), TOUCH_LONG_PRESS_MS);
        }}
        onTouchEnd={clearLongPress}
        onTouchCancel={clearLongPress}
        className={clsx(
          'inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors',
          'hover:bg-[#e0ecf8] hover:text-[#468AD4]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#468AD4] focus-visible:ring-offset-1',
          'dark:text-[#9BAEC2] dark:hover:bg-white/10 dark:hover:text-[#7BB0E8]',
        )}
      >
        <Info className="h-3.5 w-3.5" aria-hidden="true" />
      </button>

      {open && position && createPortal(
        <div
          id={tooltipId}
          ref={tooltipRef}
          role="tooltip"
          data-placement={position.placement}
          className={clsx(
            'fixed z-[100000] rounded border border-[#dfe3e8] bg-white px-3 py-2 text-xs leading-5 text-[#212529]',
            'shadow-[0_8px_24px_rgba(30,32,37,0.18)]',
            'dark:border-white/10 dark:bg-[#1A1A1A] dark:text-[#F4F6F9]',
          )}
          style={{
            // Exactly one of these is set; see `TooltipPosition`.
            top: position.top,
            bottom: position.bottom,
            left: position.left,
            maxWidth: MAX_WIDTH,
            width: 'max-content',
          }}
        >
          {resolvedContent}
        </div>,
        document.body,
      )}
    </span>
  );
}

interface FieldLabelWithHelpProps {
  label: string;
  htmlFor?: string;
  required?: boolean;
  helpText?: ReactNode;
  className?: string;
  markerClassName?: string;
  /** Keep the help icon always visible (tooltip text still hover-only). */
  helpAlwaysVisible?: boolean;
}

export function FieldLabelWithHelp({
  label,
  htmlFor,
  required = false,
  helpText,
  className,
  markerClassName = 'ml-1 text-red-600',
  helpAlwaysVisible = false,
}: FieldLabelWithHelpProps) {
  // Several legacy callsites pass the marker inline in the label string
  // (e.g. `label="Amount *"`) instead of using the `required` prop. That
  // renders the asterisk in the label's gray color, not the red we want
  // for the required-field convention. Strip a trailing ` *` / `*` here
  // and treat it as `required`, so a single component-level rule yields
  // a consistent red marker regardless of which pattern the caller used.
  const trailingMarker = /\s*\*\s*$/.test(label);
  const cleanedLabel = trailingMarker ? label.replace(/\s*\*\s*$/, '') : label;
  const showMarker = required || trailingMarker;
  return (
    <div className="mb-2 flex min-h-[1.75rem] items-center gap-1.5">
      <label
        htmlFor={htmlFor}
        className={clsx('block text-[13.2px] font-medium text-[#212529] dark:text-white', className)}
      >
        {cleanedLabel}
        {showMarker && <span className={markerClassName}>*</span>}
      </label>
      <HelpTooltip label={cleanedLabel} content={helpText} alwaysVisible={helpAlwaysVisible} />
    </div>
  );
}

export function defaultSettingsHelp(label: string): string | null {
  const pathname = typeof window === 'undefined' ? undefined : window.location.pathname;
  return getSettingsTooltipCopy(label, pathname);
}
